import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import CallFormModal from '../../components/call-management/CallFormModal';
import FollowUpFormModal from '../../components/call-management/FollowUpFormModal';
import CallDetailsModal from '../../components/call-management/CallDetailsModal';

const VENDORS_FILTERS_KEY = 'vendors_page_filters_v1';
const outcomeLabel = {
  interested: 'Interested',
  not_interested: 'Not Interested',
  call_later: 'Call Later',
  no_answer: 'No Answer'
};

const Vendors = () => {
  const { user } = useAuth();
  const getStoredFilters = () => {
    try {
      const raw = sessionStorage.getItem(VENDORS_FILTERS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      return {};
    }
  };
  const storedFilters = getStoredFilters();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState(storedFilters.searchKeyword || '');
  const [searchInput, setSearchInput] = useState(storedFilters.searchKeyword || '');
  const searchDebounceRef = useRef(null);
  const [dateRange, setDateRange] = useState(storedFilters.dateRange || '');
  const [verifyStatus, setVerifyStatus] = useState(storedFilters.verifyStatus || '');
  const [inListStatus, setInListStatus] = useState(storedFilters.inListStatus || '');
  const [lowBalance, setLowBalance] = useState(storedFilters.lowBalance || 'All');
  const [currentPage, setCurrentPage] = useState(storedFilters.currentPage || 1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(storedFilters.recordsPerPage || 50);
  const dateInputRef = useRef(null);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [detailsFollowUps, setDetailsFollowUps] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [callSubmitting, setCallSubmitting] = useState(false);
  const [callForm, setCallForm] = useState({
    id: '',
    name: '',
    phone: '',
    email: '',
    businessName: '',
    source: 'vendor',
    callType: 'outgoing',
    callStatus: '',
    note: '',
    nextFollowDateTime: '',
    followUpStatus: 'pending',
    followUpOutcome: 'call_later'
  });
  const [followUpForm, setFollowUpForm] = useState({
    id: '',
    callId: '',
    status: 'pending',
    outcome: '',
    note: '',
    callStatus: 'callback',
    nextFollowDateTime: ''
  });
  const [associationModal, setAssociationModal] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletedFilter, setDeletedFilter] = useState('0');

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedVendorForImage, setSelectedVendorForImage] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        draw: 1,
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: searchKeyword,
        date: dateRange,
        type: verifyStatus,
        inList: inListStatus,
        lawbalance: lowBalance,
        isDeletedFilter: deletedFilter
      };
      
      const response = await apiService.post('vendorsV1', params);
      if (response && response.data) {
        const rows = Array.isArray(response.data) ? response.data : [];
        const total = Number(response.recordsFiltered ?? response.recordsTotal ?? 0);
        setVendors(rows);
        setTotalRecords(Number.isFinite(total) ? total : 0);

        // If current page has no rows but total exists, go back to page 1.
        if (rows.length === 0 && total > 0 && currentPage > 1) {
          setCurrentPage(1);
        }
      }
    } catch (error) {
      toast.error('Failed to load vendors');
      console.error('Vendors fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, searchKeyword, dateRange, verifyStatus, inListStatus, lowBalance, deletedFilter]);

  const checkVendorAssociations = async (id, name) => {
    try {
      const response = await apiService.get(`checkUserAssociations/${id}`);
      setAssociationModal({ id, name, response });
    } catch (error) {
      toast.error('Error checking vendor associations');
    }
  };

  const handleRestoreVendor = async (vendorId) => {
    try {
      const response = await apiService.put(`restoreUser/${vendorId}`);
      if (response.status || response.success) {
        toast.success('Vendor restored successfully');
        fetchVendors();
      } else {
        toast.error(response.message || 'Failed to restore vendor');
      }
    } catch (error) {
      toast.error('Error restoring vendor');
    }
  };

  const handleDeleteVendor = async () => {
    setDeleteLoading(true);
    try {
      const response = await apiService.delete(`deleteUser/${associationModal.id}`);
      if (response.success || response.status) {
        toast.success('Vendor deleted successfully');
        setAssociationModal(null);
        fetchVendors();
      } else {
        toast.error(response.message || 'Failed to delete vendor');
      }
    } catch (error) {
      toast.error('Error deleting vendor');
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        VENDORS_FILTERS_KEY,
        JSON.stringify({
          searchKeyword,
          dateRange,
          verifyStatus,
          inListStatus,
          lowBalance,
          currentPage,
          recordsPerPage
        })
      );
    } catch (error) {
      // ignore storage errors
    }
  }, [searchKeyword, dateRange, verifyStatus, inListStatus, lowBalance, currentPage, recordsPerPage]);

  useEffect(() => {
    if (dateInputRef.current && window.$) {
      window.$(dateInputRef.current).daterangepicker({
        autoUpdateInput: false,
        locale: { format: 'DD/MM/YYYY', cancelLabel: 'Clear' }
      });
      window.$(dateInputRef.current).on('apply.daterangepicker', (ev, picker) => {
        const range = picker.startDate.format('DD/MM/YYYY') + ' - ' + picker.endDate.format('DD/MM/YYYY');
        setDateRange(range);
        setCurrentPage(1);
      });
      window.$(dateInputRef.current).on('cancel.daterangepicker', () => {
        setDateRange('');
        setCurrentPage(1);
      });
    }
  }, []);

  const handleSearch = (value) => {
    setSearchInput(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchKeyword(value);
      setCurrentPage(1);
    }, 500);
  };

  const toggleVendorStatus = async (vendorId) => {
    try {
      const response = await apiService.get(`vendorStatus/${vendorId}`);
      if (response.status) {
        toast.success(response.message || 'Vendor status updated');
        setVendors(prevVendors => 
          prevVendors.map(v => 
            v._id === vendorId ? { ...v, block: v.block === 1 ? 0 : 1 } : v
          )
        );
      }
    } catch (error) {
      toast.error('Failed to update vendor status');
    }
  };

  const toggleTestingStatus = async (vendorId) => {
    try {
      const response = await apiService.get(`vendorStatusTesting/${vendorId}`);
      if (response.status) {
        toast.success('Vendor testing status changed!');
        setVendors(prevVendors => 
          prevVendors.map(v => 
            v._id === vendorId ? { ...v, isTesting: v.isTesting === 1 ? 0 : 1 } : v
          )
        );
      }
    } catch (error) {
      toast.error('Failed to update testing status');
    }
  };

  const openImageModal = (vendorData) => {
    setSelectedVendorForImage(vendorData);
    setImagePreview(vendorData.image ? `${process.env.REACT_APP_MEDIA_URL}${vendorData.image}` : null);
    setNewImageFile(null);
    setImageModalOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (!newImageFile) {
      toast.error("Please select an image");
      return;
    }
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', newImageFile);
      const response = await apiService.postFormData(`updateUserImage/${selectedVendorForImage._id}`, formData);
      if (response.status) {
        toast.success(response.message || 'Image updated successfully');
        setVendors(prevVendors => 
          prevVendors.map(v => 
            v._id === selectedVendorForImage._id ? { ...v, image: response.data.image } : v
          )
        );
        setImageModalOpen(false);
      } else {
        toast.error(response.message || 'Failed to update image');
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };


  const toggleInListStatus = async (vendorId) => {
    try {
      const response = await apiService.get(`changeListStatus/${vendorId}`);
      if (response.status) {
        toast.success(response.message || 'List status updated');
        setVendors(prevVendors => 
          prevVendors.map(v => 
            v._id === vendorId ? { ...v, inList: v.inList === 1 ? 0 : 1 } : v
          )
        );
      }
    } catch (error) {
      toast.error('Failed to update list status');
    }
  };

  const updateRechargeValue = async (vendorId, value) => {
    try {
      const response = await apiService.post('setRechargeValue', {
        id: vendorId,
        rechargeValue: value
      });
      if (response.status) {
        toast.success(response.message || 'Recharge value updated');
        setVendors(prevVendors => 
          prevVendors.map(v => 
            v._id === vendorId ? { ...v, rechargeValue: value } : v
          )
        );
      }
    } catch (error) {
      toast.error('Failed to update recharge value');
    }
  };

  const getVerifyStatusBadge = (status) => {
    const badges = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      inProgress: 'bg-[#009ef7]/10 text-[#009ef7]',
      rejected: 'bg-red-100 text-red-800'
    };
    const text = {
      approved: 'Approved',
      pending: 'Pending', 
      inProgress: 'In Progress',
      rejected: 'Rejected'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {text[status] || 'Unknown'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata'
    });
  };

  const formatDateTime = (value) => {
    if (!value) return '--';
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const normalizePhone = (value) => String(value || '').replace(/\D/g, '');

  const isSamePhone = (left, right) => {
    const leftPhone = normalizePhone(left);
    const rightPhone = normalizePhone(right);
    if (!leftPhone || !rightPhone) return false;
    return leftPhone === rightPhone || leftPhone.endsWith(rightPhone) || rightPhone.endsWith(leftPhone);
  };

  const buildCallPayloadFromVendor = (vendor) => ({
    name: `${vendor?.first_name || ''} ${vendor?.last_name || ''}`.trim() || vendor?.business_name || 'Vendor',
    phone: vendor?.phone || '',
    email: vendor?.email || '',
    businessName: vendor?.business_name || '',
    source: 'vendor',
    callType: 'outgoing',
    callStatus: '',
    duration: undefined,
    note: ''
  });

  const findVendorCallByPhone = async (phone) => {
    const normalized = normalizePhone(phone);
    if (!normalized) return null;

    try {
      const searches = [phone, normalized].map((value) => String(value || '')).filter(Boolean);
      for (const search of searches) {
        const response = await apiService.post('call/list', { start: 0, length: 200, search: String(search) });
        const rows = Array.isArray(response?.data) ? response.data : [];
        const exactMatch = rows.find((row) => {
          const rowPhone = row?.contactPhone || row?.contactId?.phone || row?.rawData?.contactId?.phone;
          return isSamePhone(rowPhone, normalized);
        });
        if (exactMatch) {
          return exactMatch?.rawData || exactMatch;
        }
      }
    } catch (error) {
      return null;
    }

    return null;
  };

  const loadCallFollowUps = async (callId) => {
    const response = await apiService.post('call/followup/list', { start: 0, length: 200 });
    const rows = Array.isArray(response?.data) ? response.data : [];
    return rows.filter((row) => row?.callId?._id === callId);
  };

  const openCreateCallModal = async (vendor) => {
    try {
      const existingCall = await findVendorCallByPhone(vendor?.phone);
      const payload = buildCallPayloadFromVendor(vendor);
      setCallForm({
        id: existingCall?._id || '',
        name: payload.name,
        phone: payload.phone,
        email: payload.email,
        businessName: payload.businessName,
        source: 'vendor',
        callType: existingCall?.callType || 'outgoing',
        callStatus: existingCall?.lastFollowUp?.callStatus || '',
        note: existingCall?.note || '',
        nextFollowDateTime: existingCall?.lastFollowUp?.nextFollowDateTime || '',
        followUpStatus: existingCall?.lastFollowUp?.status || 'pending',
        followUpOutcome: existingCall?.lastFollowUp?.outcome || ''
      });
      setCallModalOpen(true);
    } catch (error) {
      toast.error('Failed to load call data');
    }
  };

  const saveCall = async (e) => {
    e.preventDefault();
    try {
      setCallSubmitting(true);
      const isCreate = !callForm.id;
      if (isCreate && callForm.followUpOutcome === 'callback' && !callForm.nextFollowDateTime) {
        toast.error('Next follow-up date and time is required for callback outcome');
        return;
      }

      const payload = {
        name: callForm.name,
        phone: callForm.phone,
        email: callForm.email,
        businessName: callForm.businessName,
        source: 'vendor',
        callType: callForm.callType,
        note: callForm.note
      };

      if (isCreate) {
        payload.nextFollowDateTime = callForm.followUpOutcome === 'callback' ? callForm.nextFollowDateTime : undefined;
        payload.outcome = callForm.followUpOutcome || undefined;
        payload.callStatus = callForm.callStatus || undefined;
      }

      if (callForm.id) {
        await apiService.put(`call/update/${callForm.id}`, payload);
        toast.success('Call updated successfully');
      } else {
        await apiService.post('call/create', payload);
        toast.success('Call created successfully');
      }
      setCallModalOpen(false);
      setCallForm({
        id: '',
        name: '',
        phone: '',
        email: '',
        businessName: '',
        source: 'vendor',
        callType: 'outgoing',
        callStatus: '',
        note: '',
        nextFollowDateTime: '',
        followUpStatus: 'pending',
        followUpOutcome: ''
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save call');
    } finally {
      setCallSubmitting(false);
    }
  };

  const openFollowUpModalForVendor = async (vendor) => {
    try {
      const existingCall = await findVendorCallByPhone(vendor?.phone);
      if (!existingCall?._id) {
        toast.error('Create call first for this vendor');
        return;
      }
      setFollowUpForm({
        callId: existingCall._id,
        status: 'pending',
        outcome: '',
        note: '',
        callStatus: 'callback',
        nextFollowDateTime: ''
      });
      setFollowUpModalOpen(true);
    } catch (error) {
      toast.error('Failed to open follow-up');
    }
  };

  const saveFollowUp = async (e) => {
    e.preventDefault();
    if (!followUpForm.callId) {
      toast.error('Call is required');
      return;
    }
    if (followUpForm.outcome === 'callback' && !followUpForm.nextFollowDateTime) {
      toast.error('Next follow-up date and time is required for callback outcome');
      return;
    }
    try {
      if (followUpForm.id) {
        await apiService.put(`call/followup/update/${followUpForm.id}`, followUpForm);
        toast.success('Follow-up updated successfully');
      } else {
        await apiService.post('call/followup/create', followUpForm);
        toast.success('Follow-up created successfully');
      }
      setFollowUpModalOpen(false);
      setFollowUpForm({ id: '', callId: '', status: 'pending', outcome: '', note: '', callStatus: 'callback', nextFollowDateTime: '' });
      if (selectedCall?._id) {
        const updated = await loadCallFollowUps(selectedCall._id);
        setDetailsFollowUps(updated);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save follow-up');
    }
  };

  const openEditFollowUpForVendor = (followUp) => {
    setFollowUpForm({
      id: followUp?._id || '',
      callId: followUp?.callId?._id || followUp?.callId || selectedCall?._id || '',
      status: followUp?.status || 'pending',
      outcome: followUp?.outcome || '',
      note: followUp?.note || '',
      callStatus: followUp?.callStatus || '',
      nextFollowDateTime: followUp?.nextFollowDateTime || followUp?.followUpDateTime || ''
    });
    setFollowUpModalOpen(true);
  };

  const deleteFollowUpForVendor = async (followUp) => {
    if (!followUp?._id) return;
    const confirmed = window.confirm('Are you sure you want to delete this follow-up?');
    if (!confirmed) return;
    try {
      await apiService.delete(`call/followup/delete/${followUp._id}`);
      toast.success('Follow-up deleted successfully');
      if (selectedCall?._id) {
        const updated = await loadCallFollowUps(selectedCall._id);
        setDetailsFollowUps(updated);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete follow-up');
    }
  };

  const openViewCallModal = async (vendor) => {
    try {
      setDetailsLoading(true);
      const existingCall = await findVendorCallByPhone(vendor?.phone);
      if (!existingCall?._id) {
        toast.error('No call found for this vendor');
        setDetailsLoading(false);
        return;
      }
      setSelectedCall(existingCall);
      setViewModalOpen(true);
      const followUps = await loadCallFollowUps(existingCall._id);
      setDetailsFollowUps(followUps);
    } catch (error) {
      toast.error('Failed to load call details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-0">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Filters Bar */}
          <div className="bg-gradient-to-r from-slate-50 to-sky-50/40 p-4 sm:p-6 border-b border-gray-200">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="relative flex-1 min-w-[220px]">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Search</label>
                <i className="bi bi-search absolute left-3 top-[34px] -translate-y-1/2 text-slate-400"></i>
                <input
                  type="text"
                  placeholder="Type Name, Phone or Email"
                  value={searchInput}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl bg-white text-sm focus:ring-2 focus:ring-[#009ef7]/25 focus:border-[#009ef7]"
                />
              </div>

              <div className="relative min-w-[220px]">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Date Range</label>
                <i className="bi bi-calendar3 absolute left-3 top-[34px] -translate-y-1/2 text-slate-400"></i>
                <input
                  ref={dateInputRef}
                  type="text"
                  value={dateRange}
                  readOnly
                  placeholder="DD/MM/YYYY - DD/MM/YYYY"
                  className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-xl bg-white text-sm cursor-pointer focus:ring-2 focus:ring-[#009ef7]/25 focus:border-[#009ef7]"
                />
              </div>

              <div className="min-w-[170px]">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Verification</label>
                <select
                  value={verifyStatus}
                  onChange={(e) => { setVerifyStatus(e.target.value); setCurrentPage(1); }}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-xl bg-white text-sm focus:ring-2 focus:ring-[#009ef7]/25"
                >
                  <option value="">Verify Status</option>
                  <option value="1">Verified</option>
                  <option value="0">Pending</option>
                  <option value="2">Rejected</option>
                </select>
              </div>

              {user?.role === 'admin' && (
                <>
                  <div className="min-w-[150px]">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">In List</label>
                    <select
                      value={inListStatus}
                      onChange={(e) => { setInListStatus(e.target.value); setCurrentPage(1); }}
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-xl bg-white text-sm focus:ring-2 focus:ring-[#009ef7]/25"
                    >
                      <option value="1">All</option>
                      <option value="yes">In List</option>
                      <option value="no">Not In List</option>
                    </select>
                  </div>

                  <div className="min-w-[160px]">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Balance</label>
                    <select
                      value={lowBalance}
                      onChange={(e) => { setLowBalance(e.target.value); setCurrentPage(1); }}
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-xl bg-white text-sm focus:ring-2 focus:ring-[#009ef7]/25"
                    >
                      <option value="All">All</option>
                      <option value="lawbalance">Low Balance</option>
                    </select>
                  </div>

                  <div className="min-w-[150px]">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Deleted Status</label>
                    <select
                      value={deletedFilter}
                      onChange={(e) => { setDeletedFilter(e.target.value); setCurrentPage(1); }}
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-xl bg-white text-sm focus:ring-2 focus:ring-[#009ef7]/25"
                    >
                      <option value="0">Active Vendors</option>
                      <option value="1">Deleted Vendors</option>
                      <option value="All">All Vendors</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Table Section */}
          <div className="overflow-x-auto rounded-b-xl border-t border-slate-100">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
                <span className="ml-2 text-gray-600">Loading vendors...</span>
              </div>
            ) : vendors.length === 0 ? (
              <div className="text-center py-12">
                <i className="bi bi-shop text-4xl text-gray-300 mb-2"></i>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No Vendors Found</h3>
                <p className="text-gray-500">No vendors match your current search criteria.</p>
              </div>
            ) : (
              <table className="min-w-full text-xs sm:text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">#</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Vendor</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Business</th>

                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Address</th>
                    
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    {user?.role === 'admin' && (
                      <>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Call Actions</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Flags</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Recharge</th>
                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Delete</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {vendors.map((vendorData, index) => (
                    <tr key={vendorData._id} className="hover:bg-sky-50/40 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-[#009ef7]">
                        {(currentPage - 1) * recordsPerPage + index + 1}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div 
                            onClick={() => openImageModal(vendorData)}
                            className="group relative h-11 w-11 flex-shrink-0 cursor-pointer overflow-hidden rounded-full ring-2 ring-slate-100"
                            title="Click to edit image"
                          >
                            {vendorData.image ? (
                              <img
                                className="h-full w-full object-cover group-hover:opacity-40 transition-opacity"
                                src={`${process.env.REACT_APP_MEDIA_URL}${vendorData.image}`}
                                alt={vendorData.first_name}
                              />
                            ) : (
                              <div className="h-full w-full bg-slate-100 flex items-center justify-center group-hover:opacity-40 transition-opacity">
                                <i className="bi bi-person-fill text-slate-500"></i>
                              </div>
                            )}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                              <i className="bi bi-camera-fill text-white text-xs"></i>
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-semibold text-slate-900">{vendorData.first_name}</div>
                            <div className="text-xs font-semibold text-slate-700 break-all">{vendorData.email || '-'}</div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-semibold text-slate-600">{vendorData.phone || '-'}</span>
                              {vendorData.phone && (
                                <a
                                  href={`tel:${vendorData.phone}`}
                                  className="inline-flex items-center justify-center w-5 h-5 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors"
                                  title={`Call ${vendorData.phone}`}
                                >
                                  <i className="bi bi-telephone-fill text-[9px]"></i>
                                </a>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500">({formatDate(vendorData.createdAt)})</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {user?.role === 'admin' ? (
                            <Link to={`/vendorWallet/${vendorData._id}`} className="text-sm text-gray-900 hover:text-[#009ef7] font-medium">
                              {vendorData.business_name || '-'}
                            </Link>
                          ) : (
                            <span className="text-sm text-gray-900 font-medium">
                              {vendorData.business_name || '-'}
                            </span>
                          )}
                          {vendorData.slug && (
                            <a 
                              href={`https://www.travellinglark.com/agent/${vendorData.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block px-2 py-1 text-[11px] font-semibold bg-cyan-100 text-cyan-800 rounded-md hover:bg-cyan-200 w-fit"
                            >
                              <i className="bi bi-building"></i>View Profile
                            </a>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3 align-top max-w-[460px] whitespace-normal break-words">
                        <div className="text-sm text-slate-900 whitespace-normal break-words">{vendorData.address || '-'}</div>
                        <div className="text-xs text-slate-500 whitespace-normal break-words">
                          {vendorData.stateId?.name && `${vendorData.stateId.name}, `}
                          {vendorData.countryId?.name || '--'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getVerifyStatusBadge(vendorData.verify)}
                      </td>
                      {user?.role === 'admin' && (
                        <>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <div className="flex flex-col gap-1">
                              <Link 
                                to={`/createTransaction/${vendorData._id}`} 
                                className="inline-block px-2 py-1 text-[11px] font-semibold bg-[#009ef7]/10 text-[#009ef7] rounded-md hover:bg-blue-200 text-center"
                              >
                                <i className="bi bi-cart mr-1"></i>Transaction
                              </Link>
                              <Link 
                                to={`/vendorWallet/${vendorData._id}`} 
                                className="inline-block px-2 py-1 text-[11px] font-semibold bg-rose-100 text-rose-800 rounded-md hover:bg-rose-200 text-center"
                              >
                                <i className="bi bi-wallet mr-1"></i>Wallet ({vendorData.walletBalance || '0'})
                              </Link>
                              <Link
                                to={`/vendor/activity/${vendorData._id}`}
                                className="inline-block px-2 py-1 text-[11px] font-semibold bg-violet-100 text-violet-800 rounded-md hover:bg-violet-200 text-center"
                              >
                                <i className="bi bi-activity mr-1"></i>Activity
                              </Link>

                              {vendorData.autoBuy === true && (
                                <span className="inline-block px-2 py-1 text-[11px] font-semibold bg-emerald-100 text-emerald-800 rounded-md text-center">
                                  <i className="bi bi-lightning-charge mr-1"></i>AutoBuy
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                onClick={() => openCreateCallModal(vendorData)}
                                className="inline-block px-2 py-1 text-[11px] font-semibold bg-indigo-100 text-indigo-800 rounded-md hover:bg-indigo-200 text-center"
                              >
                                <i className="bi bi-telephone-plus mr-1"></i>Create Call
                              </button>
                              <button
                                type="button"
                                onClick={() => openFollowUpModalForVendor(vendorData)}
                                className="inline-block px-2 py-1 text-[11px] font-semibold bg-amber-100 text-amber-800 rounded-md hover:bg-amber-200 text-center"
                              >
                                <i className="bi bi-calendar-check mr-1"></i>Follow-up
                              </button>
                              <button
                                type="button"
                                onClick={() => openViewCallModal(vendorData)}
                                className="inline-block px-2 py-1 text-[11px] font-semibold bg-teal-100 text-teal-800 rounded-md hover:bg-teal-200 text-center"
                              >
                                <i className="bi bi-eye mr-1"></i>View Call
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap align-middle">
                            <div className="space-y-1.5 min-w-[140px]">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] text-slate-600">Blocked</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={vendorData.block === 1}
                                    onChange={() => toggleVendorStatus(vendorData._id)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-4 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-600"></div>
                                </label>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] text-slate-600">Testing</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={vendorData.isTesting === 1}
                                    onChange={() => toggleTestingStatus(vendorData._id)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-4 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                                </label>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] text-slate-600">In List</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={vendorData.inList === 1}
                                    onChange={() => toggleInListStatus(vendorData._id)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-4 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                                </label>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <select 
                              value={vendorData.rechargeValue || ''}
                              onChange={(e) => updateRechargeValue(vendorData._id, e.target.value)}
                              className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white"
                            >
                              <option value="">Select</option>
                              <option value="1000">1000</option>
                              <option value="2000">2000</option>
                              <option value="3000">3000</option>
                              <option value="4000">4000</option>
                            </select>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center">
                            {vendorData.isDeleted ? (
                              <button
                                type="button"
                                onClick={() => handleRestoreVendor(vendorData._id)}
                                className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-md hover:bg-emerald-200 transition-colors"
                                title="Restore Vendor"
                              >
                                <i className="bi bi-arrow-counterclockwise mr-1"></i>Restore
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => checkVendorAssociations(vendorData._id, vendorData.business_name || `${vendorData.first_name || ''} ${vendorData.last_name || ''}`.trim() || 'Vendor')}
                                className="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Vendor"
                              >
                                <i className="bi bi-trash text-lg"></i>
                              </button>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalRecords > 0 && vendors.length > 0 && (
            <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs sm:text-sm text-gray-700">Show</span>
                <select value={recordsPerPage} onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white text-gray-700">
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs sm:text-sm text-gray-700">entries</span>
                <span className="text-xs sm:text-sm text-gray-500 hidden md:inline">({(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords})</span>
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-center">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">&laquo;</button>
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 border rounded text-xs sm:text-sm ${currentPage === page ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-gray-300 hover:bg-gray-100'}`}>{page}</button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2 text-gray-500">...</span>;
                  }
                  return null;
                })}
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">&raquo;</button>
              </div>
            </div>
          )}
        </div>
      </div>
      <CallFormModal
        open={callModalOpen}
        form={callForm}
        setForm={setCallForm}
        onClose={() => setCallModalOpen(false)}
        onSubmit={saveCall}
        submitting={callSubmitting}
      />

      <FollowUpFormModal
        open={followUpModalOpen}
        form={followUpForm}
        setForm={setFollowUpForm}
        onClose={() => setFollowUpModalOpen(false)}
        onSubmit={saveFollowUp}
      />

      <CallDetailsModal
        open={viewModalOpen}
        call={selectedCall}
        followUps={detailsFollowUps}
        loading={detailsLoading}
        onClose={() => setViewModalOpen(false)}
        onAddFollowUp={() => {
          setFollowUpForm({ id: '', callId: selectedCall?._id, status: 'pending', outcome: '', note: '', callStatus: 'callback', nextFollowDateTime: '' });
          setFollowUpModalOpen(true);
        }}
        formatDateTime={formatDateTime}
        outcomeLabel={outcomeLabel}
        onEditFollowUp={openEditFollowUpForVendor}
        onDeleteFollowUp={deleteFollowUpForVendor}
      />

      {associationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h5 className="text-base font-semibold">Delete Vendor: {associationModal.name}</h5>
              <button onClick={() => setAssociationModal(null)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {associationModal.response.status ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
                  <i className="bi bi-check-circle-fill"></i>
                  This vendor can be safely deleted.
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 text-yellow-700 rounded-lg mb-3">
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    This vendor has associations and cannot be deleted:
                  </div>
                  {associationModal.response.data?.associations?.map((assoc, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg mb-3">
                      <div className="px-3 py-2 bg-gray-50 border-b font-medium text-sm font-semibold">
                        {assoc.type.replace(/_/g, ' ').toUpperCase()} ({assoc.count})
                      </div>
                      <div className="p-3">
                        {assoc.data.slice(0, 5).map((item, j) => (
                          <div key={j} className="text-xs text-gray-600 py-1">
                            {item.name || item.title || item.templateName || item.bookingId || item.transactionId || item.first_name || `ID: ${item._id}`}
                          </div>
                        ))}
                        {assoc.count > 5 && <div className="text-xs text-gray-400">... and {assoc.count - 5} more</div>}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button onClick={() => setAssociationModal(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              {associationModal.response.status && (
                <button
                  onClick={handleDeleteVendor}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete Vendor'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {imageModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl border border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Update Profile Image</h3>
                <p className="text-[11px] text-slate-500">Upload a new image for {selectedVendorForImage?.first_name}</p>
              </div>
              <button type="button" onClick={() => setImageModalOpen(false)} className="h-8 w-8 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                <i className="bi bi-x"></i>
              </button>
            </div>
            <form onSubmit={handleImageUpload} className="px-4 py-4 space-y-4">
              <div className="flex flex-col items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center relative">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <i className="bi bi-person-fill text-slate-400 text-4xl"></i>
                  )}
                </div>
                <label className="mt-3 cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-sm">
                  <i className="bi bi-upload mr-1.5"></i>
                  Choose File
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                </label>
              </div>
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button type="button" onClick={() => setImageModalOpen(false)} className="px-4 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-xs font-semibold">
                  Cancel
                </button>
                <button type="submit" disabled={imageUploading || !newImageFile} className="px-4 py-2 bg-[#009ef7] text-white hover:bg-[#009ef7]/90 disabled:opacity-50 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                  {imageUploading ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default Vendors;
